import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlineShieldCheck, HiOutlineMapPin, HiOutlineUserGroup, HiOutlineClipboardDocumentCheck, HiOutlineBeaker, HiOutlineCheckCircle, HiOutlineDocumentMagnifyingGlass, HiOutlineWrenchScrewdriver } from 'react-icons/hi2';
import { workPermitService, plantService, lineService, machineService, userService } from '../services';
import WorkPermitStep1Basic from './workpermit/WorkPermitStep1Basic';
import WorkPermitStep2Equipment from './workpermit/WorkPermitStep2Equipment';
import WorkPermitStep3Hazards from './workpermit/WorkPermitStep3Hazards';
import WorkPermitStep4Personnel from './workpermit/WorkPermitStep4Personnel';
import WorkPermitStep5SafetyChecks from './workpermit/WorkPermitStep5SafetyChecks';
import WorkPermitStep6Isolations from './workpermit/WorkPermitStep6Isolations';
import WorkPermitStep7GasTests from './workpermit/WorkPermitStep7GasTests';
import WorkPermitStep8Review from './workpermit/WorkPermitStep8Review';
import PageLoading from '../components/common/PageLoading';
import './WorkPermitWizard.css';

const steps = [
  { id: 1, name: 'Basic', icon: HiOutlineShieldCheck },
  { id: 2, name: 'Equipment', icon: HiOutlineWrenchScrewdriver },
  { id: 3, name: 'Hazards', icon: HiOutlineMapPin },
  { id: 4, name: 'Personnel', icon: HiOutlineUserGroup },
  { id: 5, name: 'Safety Checks', icon: HiOutlineClipboardDocumentCheck },
  { id: 6, name: 'Isolations', icon: HiOutlineDocumentMagnifyingGlass },
  { id: 7, name: 'Gas Tests', icon: HiOutlineBeaker },
  { id: 8, name: 'Review', icon: HiOutlineCheckCircle },
];

const initialFormState = {
  permitNumber: '',
  permitTypeId: '',
  workOrderId: '',
  status: 'Draft',
  location: '',
  area: '',
  equipmentId: '',
  plantId: '',
  lineId: '',
  workDescription: '',
  validFrom: '',
  validTo: '',
  hazardsJson: '[]',
  personnelJson: '[]',
  safetyChecksJson: '[]',
  isolationsJson: '[]',
  gasTestsJson: '[]',
};

const statusOptions = ['Draft', 'Under Review', 'Issued', 'Active', 'Work in Progress', 'Closed', 'Cancelled'];

const WorkPermitWizard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [permitTypes, setPermitTypes] = useState([]);
  const [safetyChecks, setSafetyChecks] = useState([]);
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);

  const loadMasters = useCallback(async () => {
    try {
      setLoading(true);
      const [types, checks, plantData, lineData, machineData, userData] = await Promise.all([
        workPermitService.getPermitTypes().catch(() => []),
        workPermitService.getSafetyChecks().catch(() => []),
        plantService.getAllPlants(),
        lineService.getAllLines(),
        machineService.getAllMachines(),
        userService.getAllUsers(),
      ]);
      setPermitTypes(types || []);
      setSafetyChecks(checks || []);
      setPlants(plantData || []);
      setLines(lineData || []);
      setMachines(machineData || []);
      setUsers(userData || []);

      // Auto-select plant if only 1 exists (for new permits)
      if (!isEdit && plantData?.length === 1) {
        setFormData(prev => ({ ...prev, plantId: plantData[0].plantId.toString() }));
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load master data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPermit = useCallback(async () => {
    if (!isEdit) return;
    try {
      setLoading(true);
      const permit = await workPermitService.getWorkPermitById(id);
      if (permit) {
        setFormData({
          permitNumber: permit.permitNumber || '',
          permitTypeId: permit.permitTypeId?.toString() || '',
          workOrderId: permit.workOrderId?.toString() || '',
          status: permit.status || 'Draft',
          location: permit.location || '',
          area: permit.area || '',
          equipmentId: permit.equipmentId?.toString() || '',
          plantId: permit.plantId?.toString() || '',
          lineId: permit.lineId?.toString() || '',
          workDescription: permit.workDescription || '',
          validFrom: permit.validFrom ? permit.validFrom.slice(0, 16) : '',
          validTo: permit.validTo ? permit.validTo.slice(0, 16) : '',
          hazardsJson: JSON.stringify(permit.hazards || []),
          personnelJson: JSON.stringify(permit.personnel || []),
          safetyChecksJson: JSON.stringify(permit.safetyChecks || []),
          isolationsJson: JSON.stringify(permit.isolations || []),
          gasTestsJson: JSON.stringify(permit.gasTests || []),
        });
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load work permit.');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  useEffect(() => {
    if (isEdit) {
      loadPermit();
    } else {
      setLoading(false);
    }
  }, [isEdit, loadPermit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'plantId' && value) {
      localStorage.setItem('defaultPlantId', value);
    }
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleArrayChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: JSON.stringify(value) }));
  };

  const validateStep = () => {
    const errors = {};
    if (currentStep === 1) {
      if (!formData.permitTypeId) errors.permitTypeId = 'Permit type is required';
      if (!formData.validFrom) errors.validFrom = 'Valid from is required';
      if (!formData.validTo) errors.validTo = 'Valid to is required';
    }
    if (currentStep === 2) {
      if (!formData.equipmentId) errors.equipmentId = 'Equipment is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    try {
      setSubmitting(true);
      setError('');
      const payload = {
        permitNumber: formData.permitNumber,
        permitTypeId: formData.permitTypeId ? parseInt(formData.permitTypeId, 10) : null,
        workOrderId: formData.workOrderId ? parseInt(formData.workOrderId, 10) : null,
        status: formData.status,
        location: formData.location,
        area: formData.area,
        equipmentId: formData.equipmentId ? parseInt(formData.equipmentId, 10) : null,
        plantId: formData.plantId ? parseInt(formData.plantId, 10) : null,
        lineId: formData.lineId ? parseInt(formData.lineId, 10) : null,
        workDescription: formData.workDescription,
        validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : null,
        validTo: formData.validTo ? new Date(formData.validTo).toISOString() : null,
        hazards: JSON.parse(formData.hazardsJson || '[]'),
        personnel: JSON.parse(formData.personnelJson || '[]'),
        requiredSafetyCheckIds: JSON.parse(formData.safetyChecksJson || '[]').map((c) => c.checkId || c),
        isolations: JSON.parse(formData.isolationsJson || '[]'),
        gasTests: JSON.parse(formData.gasTestsJson || '[]'),
      };

      if (isEdit) {
        await workPermitService.updateWorkPermit(id, payload);
        setSuccess('Work permit updated');
      } else {
        await workPermitService.createWorkPermit(payload);
        setSuccess('Work permit created');
      }
      navigate('/home/work-permits');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save work permit.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = useMemo(() => {
    const commonProps = { formData, handleChange, handleArrayChange, formErrors, permitTypes, safetyChecks, plants, lines, machines, users };
    switch (currentStep) {
      case 1:
        return <WorkPermitStep1Basic {...commonProps} statusOptions={statusOptions} />;
      case 2:
        return <WorkPermitStep2Equipment {...commonProps} />;
      case 3:
        return <WorkPermitStep3Hazards {...commonProps} />;
      case 4:
        return <WorkPermitStep4Personnel {...commonProps} />;
      case 5:
        return <WorkPermitStep5SafetyChecks {...commonProps} />;
      case 6:
        return <WorkPermitStep6Isolations {...commonProps} />;
      case 7:
        return <WorkPermitStep7GasTests {...commonProps} />;
      case 8:
        return <WorkPermitStep8Review formData={formData} permitTypes={permitTypes} machines={machines} />;
      default:
        return null;
    }
  }, [currentStep, formData, formErrors, handleArrayChange, handleChange, machines, permitTypes, plants, lines, safetyChecks, users]);

  if (loading) {
    return (
      <div className="work-permit-wizard-page">
        <PageLoading message="Loading permit…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer work-permit-wizard-page">
      <div className="page-header">
        <div>
          <h1>{isEdit ? 'Edit Work Permit' : 'New Work Permit'}</h1>
          <p>Step-by-step wizard for issuing safe permits.</p>
        </div>
        <button className="btn-secondary" onClick={() => navigate('/home/work-permits')}>
          <HiOutlineArrowLeft className="btn-icon" />
          Back to list
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="wizard-container">
        <div className="wizard-steps-indicator">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                className={`step-item ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
                onClick={() => setCurrentStep(step.id)}
              >
                <div className="step-icon">
                  <Icon />
                </div>
                <span className="step-name">{step.name}</span>
              </div>
            );
          })}
        </div>

        <div className="wizard-content">
          <form onSubmit={handleSubmit}>
            {renderStep}
            <div className="wizard-navigation">
              {currentStep > 1 && (
                <button type="button" className="btn-secondary" onClick={handlePrev}>
                  Previous
                </button>
              )}
              {currentStep < steps.length && (
                <button type="button" className="btn-primary" onClick={handleNext}>
                  Next
                </button>
              )}
              {currentStep === steps.length && (
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Permit'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkPermitWizard;

